import { Link } from 'react-router-dom';
import { Linkedin, Instagram, Send } from 'lucide-react';
import { useState, useEffect } from 'react';
import api from '../../lib/api';
import DOMPurify from 'dompurify';

const LOGO_URL = "/assets/sios-logo.png";

// X (Twitter) icon component
const XIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [privacyUrl, setPrivacyUrl] = useState('');
  const [cookieUrl, setCookieUrl] = useState('');
  const [cookieSettingsHtml, setCookieSettingsHtml] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState(null);

  useEffect(() => {
    // Load settings to get privacy and cookie URLs
    const loadConfig = async () => {
      try {
        // First try newsletter config for privacy URL (legacy)
        const newsletterRes = await api.get('/newsletter/config/public');
        if (newsletterRes.data?.privacy_policy_url) {
          setPrivacyUrl(newsletterRes.data.privacy_policy_url);
        }
      } catch (err) {
        console.error('Newsletter config load failed:', err);
      }
      
      try {
        // Load from settings (new)
        const settingsRes = await api.get('/settings/public');
        if (settingsRes.data?.privacy_policy_url) {
          setPrivacyUrl(settingsRes.data.privacy_policy_url);
        }
        if (settingsRes.data?.cookie_policy_url) {
          setCookieUrl(settingsRes.data.cookie_policy_url);
        }
        if (settingsRes.data?.cookie_settings_html) {
          setCookieSettingsHtml(settingsRes.data.cookie_settings_html);
        }
      } catch (err) {
        console.error('Settings load failed:', err);
      }
    };
    loadConfig();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !privacyAccepted) return;
    
    setSubscribing(true);
    setSubscribeStatus(null);
    
    try {
      const res = await api.post('/newsletter/subscribe', { email });
      if (res.data.success) {
        setSubscribeStatus('success');
        setEmail('');
        setPrivacyAccepted(false);
      } else {
        setSubscribeStatus('error');
      }
    } catch (error) {
      setSubscribeStatus('error');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-sios-void border-t border-white/5 py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        {/* Newsletter Section - Full Width Centered */}
        <div className="text-center mb-12 pb-12 border-b border-white/10">
          {/* First Row - Title */}
          <div className="mb-6">
            <h4 className="font-heading font-semibold text-xl mb-2 text-foreground">Rimani Connesso</h4>
            <p className="text-muted-foreground">Iscriviti alla newsletter</p>
          </div>
          
          {/* Second Row - Form */}
          <form onSubmit={handleSubscribe} className="max-w-xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="La tua email"
                className="flex-1 px-4 py-3 text-sm rounded-lg bg-white/5 border border-white/10 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
                required
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={subscribing || !privacyAccepted}
                className="px-6 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                data-testid="newsletter-submit-btn"
              >
                <Send size={16} />
                <span>Iscriviti</span>
              </button>
            </div>
            
            {/* Privacy checkbox */}
            <label className="flex items-center justify-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-primary focus:ring-primary/50"
                data-testid="newsletter-privacy-checkbox"
              />
              <span className="text-sm text-muted-foreground">
                Dichiaro di aver preso visione della{' '}
                {privacyUrl ? (
                  <a 
                    href={privacyUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Privacy Policy
                  </a>
                ) : (
                  <span className="text-primary">Privacy Policy</span>
                )}.
              </span>
            </label>
          </form>
          {subscribeStatus === 'success' && (
            <p className="text-green-400 text-sm mt-3">Iscrizione completata!</p>
          )}
          {subscribeStatus === 'error' && (
            <p className="text-red-400 text-sm mt-3">Errore. Riprova.</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <img src={LOGO_URL} alt="SIOS" className="h-12 w-auto mb-6" />
            <p className="text-muted-foreground text-sm leading-relaxed">
              StartupItalia Open Summit - L'evento italiano dedicato all'innovazione, 
              alle startup e al futuro del business.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-foreground">
              Link Utili
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/editions" className="text-muted-foreground hover:text-primary text-sm transition-colors">
                  Edizioni
                </Link>
              </li>
              <li>
                <Link to="/speakers" className="text-muted-foreground hover:text-primary text-sm transition-colors">
                  Speaker
                </Link>
              </li>
              <li>
                <a href="https://startupitalia.eu" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary text-sm transition-colors">
                  StartupItalia
                </a>
              </li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-foreground">
              Seguici
            </h4>
            <div className="flex gap-4">
              <a 
                href="https://linkedin.com/company/startupitalia" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full glass flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-all"
                data-testid="social-linkedin"
              >
                <Linkedin size={18} />
              </a>
              <a 
                href="https://x.com/startupitalia" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full glass flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-all"
                data-testid="social-x"
              >
                <XIcon size={18} />
              </a>
              <a 
                href="https://instagram.com/startupitalia" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full glass flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-all"
                data-testid="social-instagram"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/5 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-muted-foreground text-xs">
              © {new Date().getFullYear()} StartupItalia. Tutti i diritti riservati.
            </p>
            <p className="text-muted-foreground text-xs">
              Powered by StartupItalia
            </p>
          </div>
          
          {/* Legal Links - Separate row */}
          {(privacyUrl || cookieUrl || cookieSettingsHtml) && (
            <div className="flex flex-wrap justify-center gap-4 mt-6 pt-4 border-t border-white/5">
              {privacyUrl && (
                <a 
                  href={privacyUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary text-xs transition-colors"
                  data-testid="footer-privacy-link"
                >
                  Privacy Policy
                </a>
              )}
              {cookieUrl && (
                <a 
                  href={cookieUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary text-xs transition-colors"
                  data-testid="footer-cookie-link"
                >
                  Cookie Policy
                </a>
              )}
              {cookieSettingsHtml && (
                <span 
                  className="cookie-settings-wrapper text-muted-foreground text-xs"
                  data-testid="footer-cookie-settings"
                  ref={(el) => {
                    if (el && cookieSettingsHtml) {
                      const sanitized = DOMPurify.sanitize(cookieSettingsHtml, {
                        ADD_ATTR: ['onclick', 'class', 'id', 'data-iub-open', 'data-iub-click', 'style'],
                        ADD_TAGS: ['style'],
                        RETURN_DOM: false,
                      });
                      el.textContent = '';
                      const template = document.createElement('template');
                      template.innerHTML = sanitized;
                      el.appendChild(template.content.cloneNode(true));
                    }
                  }}
                />
              )}
              {/* Force visibility of cookie settings links */}
              <style>{`
                .cookie-settings-wrapper a,
                .cookie-settings-wrapper .clickio-cmp-settings-display,
                .cookie-settings-wrapper [style*="display:none"],
                .cookie-settings-wrapper [style*="display: none"] {
                  display: inline !important;
                  color: inherit;
                }
                .cookie-settings-wrapper a:hover {
                  color: var(--primary) !important;
                }
              `}</style>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};
